import * as THREE from 'three';
import * as Astronomy from 'astronomy-engine';

let animationIndex = 0;
let sunTrajectory = [];
let sunpaths = [];
let sunMesh, sunLight, sunLabelSprite, updateSunLabel;
let totalFrames = 0;
let animationSpeed = 1;
let isSunSimulationRunning = false;
let animationFrameId;
let clock = new THREE.Clock();

export function calculateSunPositions(latitude, longitude) {
  const observer = new Astronomy.Observer(latitude, longitude, 0);
  const year = new Date().getFullYear();
  const radius = 100;
  animationIndex = 100 * 24;
  const usTimezoneOffset = -5;

  function createHourLabel(text) {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');

    ctx.font = '16px monospace';
    ctx.fillStyle = 'cyan';
    ctx.textAlign = 'center';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 6);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(8, 4, 1);

    return sprite;
  }

  let arr = [[], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], [], []];

  for (let month = 0; month < 12; month++) {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth;) {
      for (let hour = 0; hour < 24; hour++) {
        const usLocalHour = hour;
        const date = new Date(Date.UTC(year, month, day, usLocalHour - usTimezoneOffset));

        const equatorial = Astronomy.Equator(Astronomy.Body.Sun, date, observer, true, true);
        const horizontal = Astronomy.Horizon(date, observer, equatorial.ra, equatorial.dec, 'normal');

        const alt = horizontal.altitude;
        const az = horizontal.azimuth;

        const phi = THREE.MathUtils.degToRad(90 - alt);
        const theta = -THREE.MathUtils.degToRad(360 - az);

        const x = radius * Math.sin(phi) * Math.sin(theta);
        const y = radius * Math.cos(phi);
        const z = radius * Math.sin(phi) * Math.cos(theta);

        const hr = hour == 0 ? 12 : hour < 12 ? hour + 12 : hour - 12;
        if (hr === 0) {
          day += 1;
        }
        const sprite = createHourLabel(`${hr}`);
        sprite.position.set(-x, y, z);

        if (z > 0) {
          if (arr[hour]) {
            arr[hour].push(new THREE.Vector3(-x, y, z));
          }
        }
        if (z > 0) {
          sunpaths.push({
            position: new THREE.Vector3(-x, y, z),
            fullDate: {
              day: day,
              month: month,
              year: 2025,
              hour: hr,
            },
            sun: {
              altitude: alt,
              azimuth: az,
            },
          });
        }
      }
    }
  }
  return sunpaths;
}

/**
 * Smoothly move the camera to the initial top-down view:
 *   position -> (0, 0, 230)
 *   looking at (0, 0, 0)
 *
 * Both position and rotation are interpolated smoothly.
 * No snap/lookAt at the end – orientation is reached via slerp.
 *
 * Also: dispatch a 'change' event on controls so CubeManager
 *       will update the cube + compass while animating.
 */
function animateCameraToInitialView(camera, controls) {
  if (!camera || !controls) {
    console.warn('animateCameraToInitialView: missing camera or controls');
    return;
  }

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // YOUR IDEAL CAM ORIENTATION
  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  const endPos = new THREE.Vector3(
    -1.3435,
    -156.3563,
    168.6739
  );

  const endEuler = new THREE.Euler(
    0.7475,
    -0.0058,
    -0.0063,
    'XYZ'
  );

  const endQuat = new THREE.Quaternion().setFromEuler(endEuler);

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>

  const startPos = camera.position.clone();
  const startQuat = camera.quaternion.clone();

  const startTime = performance.now();
  const duration = 1000;

  const prevEnabled = controls.enabled;
  controls.enabled = false;

  function animate() {
    const now = performance.now();
    const t = Math.min(1, (now - startTime) / duration);

    // smoothstep
    const ease = t * t * (3 - 2 * t);

    // smooth pos + rot interpolation
    camera.position.lerpVectors(startPos, endPos, ease);
    camera.quaternion.slerpQuaternions(startQuat, endQuat, ease);

    // notify orbitControls on each tick
    controls.dispatchEvent({ type: 'change' });

    if (t < 1) {
      requestAnimationFrame(animate);
    } else {
      // ---- CRITICAL PART ----
      // Do NOT set camera.position or quaternion.
      // Do NOT call controls.update().
      // Just re-enable controls and exit cleanly.

      controls.enabled = prevEnabled;

      // final sync event
      controls.dispatchEvent({ type: 'change' });
    }
  }

  requestAnimationFrame(animate);
}


// store all lines we add
let sunPathLines = [];

// NOTE: back to (scene, camera, controls, latitude, longitude)
export function initSunSimulation(scene, camera, controls, latitude, longitude) {
  if (isSunSimulationRunning) {
    // --- STOP SIMULATION ---
    cancelAnimationFrame(animationFrameId);

    if (sunMesh) {
      if (sunLabelSprite) sunMesh.remove(sunLabelSprite);
      scene.remove(sunMesh);
      sunMesh = null;
    }
    if (sunLight) {
      scene.remove(sunLight);
      sunLight = null;
    }

    // remove all stored path lines
    sunPathLines.forEach((line) => scene.remove(line));
    sunPathLines = [];

    sunTrajectory = [];
    totalFrames = 0;
    animationIndex = 0;

    isSunSimulationRunning = false;
    return;
  }

  // --- START SIMULATION ---
  isSunSimulationRunning = true;

  // Smooth move to top-down initial view (position + rotation, fully tweened)
  // and use controls.dispatchEvent('change') to keep cube/compass in sync
  if (camera && controls) {
    animateCameraToInitialView(camera, controls);
  }

  const observer = new Astronomy.Observer(latitude, longitude, 0);
  const year = new Date().getFullYear();
  const radius = 100;
  animationIndex = 100 * 24;
  const usTimezoneOffset = -5;

  // SUN MESH
  const sunGeometry = new THREE.SphereGeometry(5, 32, 32);
  const sunMaterial = new THREE.MeshBasicMaterial({ color: 0xffdd66 });
  sunMesh = new THREE.Mesh(sunGeometry, sunMaterial);
  scene.add(sunMesh);

  // SUN LIGHT
  sunLight = new THREE.DirectionalLight(0xffffff, 1);
  scene.add(sunLight);

  for (let month = 0; month < 12; month++) {
    const pathPoints = [];
    for (let hour = 0; hour < 24; hour++) {
      const usLocalHour = hour;
      const date = new Date(Date.UTC(year, month, 0, usLocalHour - usTimezoneOffset));
      const equatorial = Astronomy.Equator(Astronomy.Body.Sun, date, observer, true, true);
      const horizontal = Astronomy.Horizon(date, observer, equatorial.ra, equatorial.dec, 'normal');
      const alt = horizontal.altitude;
      const az = horizontal.azimuth;
      const phi = -THREE.MathUtils.degToRad(90 - alt);
      const theta = -THREE.MathUtils.degToRad(360 - az);
      const x = radius * Math.sin(phi) * Math.sin(theta);
      const y = radius * Math.cos(phi);
      const z = radius * Math.sin(phi) * Math.cos(theta);
      if (z > 0) {
        pathPoints.push(new THREE.Vector3(x, -y, z));
      }
    }
    const geometry = new THREE.BufferGeometry().setFromPoints(pathPoints);
    const material = new THREE.LineBasicMaterial({ color: 0xffdd66, transparent: true, opacity: 1 });
    const line = new THREE.Line(geometry, material);
    line.userData.isSunPath = true;
    if (month == 6) {
      line.position.y -= 0.8;
      scene.add(line);
      sunPathLines.push(line);
    } else if (month == 0) {
      line.position.y += 1;
      scene.add(line);
      sunPathLines.push(line);
    }
  }

  // PATHS + TRAJECTORY
  let arr = Array.from({ length: 24 }, () => []);

  for (let month = 0; month < 12; month++) {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let day = 1; day <= daysInMonth; ) {
      for (let hour = 0; hour < 24; hour++) {
        const date = new Date(Date.UTC(year, month, day, hour - usTimezoneOffset));
        const equatorial = Astronomy.Equator(Astronomy.Body.Sun, date, observer, true, true);
        const horizontal = Astronomy.Horizon(date, observer, equatorial.ra, equatorial.dec, 'normal');
        const alt = horizontal.altitude;
        const az = horizontal.azimuth;

        const phi = THREE.MathUtils.degToRad(90 - alt);
        const theta = -THREE.MathUtils.degToRad(360 - az);

        const x = radius * Math.sin(phi) * Math.sin(theta);
        const y = radius * Math.cos(phi);
        const z = radius * Math.sin(phi) * Math.cos(theta);

        const hr = hour == 0 ? 12 : hour < 12 ? hour + 12 : hour - 12;
        if (hr === 0) day += 1;

        if (z > 0) {
          if (arr[hour]) arr[hour].push(new THREE.Vector3(-x, y, z));
        }

        sunTrajectory.push({
          position: new THREE.Vector3(-x, y, z),
          date: `2025-${month + 1}-${day}T${hr}:00`,
        });
      }
    }
  }

  for (let k = 0; k < arr.length; k++) {
    const geometry = new THREE.BufferGeometry().setFromPoints(arr[k]);
    const material = new THREE.LineBasicMaterial({
      color: 0xffdd66,
      transparent: true,
      opacity: 1,
    });
    const line = new THREE.Line(geometry, material);
    line.userData.isSunPath = true;
    scene.add(line);
    sunPathLines.push(line);
  }

  totalFrames = sunTrajectory.length;

  // LABEL
  function createSunLabel(text) {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    ctx.font = '28px monospace';
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 10);

    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(25, 6, 1);

    return {
      sprite,
      updateText: (newText) => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillText(newText, canvas.width / 2, canvas.height / 2 + 10);
        texture.needsUpdate = true;
      },
    };
  }

  const label = createSunLabel('Loading...');
  sunLabelSprite = label.sprite;
  updateSunLabel = label.updateText;
  sunLabelSprite.position.set(0, 8, 0);
  sunMesh.add(sunLabelSprite);

  // ANIMATION LOOP
  function animate() {
    if (!isSunSimulationRunning) return;

    animationFrameId = requestAnimationFrame(animate);

    const delta = clock.getDelta();
    animationIndex += animationSpeed * delta;
    if (animationIndex >= totalFrames) animationIndex = 0;

    const idx = Math.floor(animationIndex);
    const nextIdx = (idx + 1) % totalFrames;
    const t = animationIndex - idx;

    const pos1 = sunTrajectory[idx].position;
    const pos2 = sunTrajectory[nextIdx].position;
    const interpolatedPos = new THREE.Vector3().lerpVectors(pos1, pos2, t);

    sunMesh.position.copy(interpolatedPos);
    sunLight.position.copy(interpolatedPos);
    sunLight.target.position.set(0, 0, 0);
    sunLight.target.updateMatrixWorld();

    const isVisible = interpolatedPos.z >= 0;
    sunMesh.visible = isVisible;
    sunLight.visible = isVisible;
    if (sunLabelSprite) sunLabelSprite.visible = isVisible;

    const currentDate = sunTrajectory[idx].date;
    if (updateSunLabel) updateSunLabel(currentDate);
  }
  animate();
}
